import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, MessageCircle, Loader2, ShoppingCart, Trash2,
  CreditCard, QrCode, FileText, User, Mail, Phone, Truck,
  Plus, Minus, Shield, Lock
} from 'lucide-react';
import { trackBeginCheckout } from '../utils/ga4';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FREIGHT_ZONES = {
  TO: 1, GO: 1, BA: 1, MA: 1, PA: 1, MT: 1,
};

const FREIGHT_TABLE = {
  1: { minPrice: 22, label: 'Região próxima',   days: '4–10 dias úteis' },
  2: { minPrice: 28, label: 'Região estendida',  days: '6–14 dias úteis' },
};

const CEP_TO_STATE = [
  [1000000, 19999999, 'SP'], [20000000, 28999999, 'RJ'], [29000000, 29999999, 'ES'],
  [30000000, 39999999, 'MG'], [40000000, 48999999, 'BA'], [49000000, 49999999, 'SE'],
  [50000000, 56999999, 'PE'], [57000000, 57999999, 'AL'], [58000000, 58999999, 'PB'],
  [59000000, 59999999, 'RN'], [60000000, 63999999, 'CE'], [64000000, 64999999, 'PI'],
  [65000000, 65999999, 'MA'], [66000000, 68899999, 'PA'], [68900000, 68999999, 'AP'],
  [69000000, 69299999, 'AM'], [69300000, 69399999, 'RR'], [69400000, 69899999, 'AM'],
  [69900000, 69999999, 'AC'], [70000000, 72799999, 'DF'], [72800000, 76799999, 'GO'],
  [76800000, 76999999, 'TO'], [77000000, 77999999, 'TO'],
  [78000000, 78899999, 'MT'], [78900000, 78999999, 'MS'], [79000000, 79999999, 'MS'],
  [80000000, 87999999, 'PR'], [88000000, 89999999, 'SC'], [90000000, 99999999, 'RS'],
];

function getStateFromCep(cep) {
  const num = parseInt(cep.replace(/\D/g, ''), 10);
  if (isNaN(num)) return null;
  for (const [min, max, state] of CEP_TO_STATE) {
    if (num >= min && num <= max) return state;
  }
  return null;
}

const CheckoutPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');
  const [cep, setCep] = useState('');
  const [shippingCost, setShippingCost] = useState(0);
  const [shippingLabel, setShippingLabel] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPartner = async () => {
      try {
        const res = await fetch(`${API_URL}/api/partners/${slug}`);
        if (res.ok) setPartner(await res.json());
      } catch (e) { console.error(e); }
    };
    fetchPartner();

    // Load cart from localStorage or from URL params (single product buy)
    const productId = searchParams.get('produto');
    const productName = searchParams.get('nome');
    const productPrice = parseFloat(searchParams.get('preco') || '0');
    const productImage = searchParams.get('imagem');
    const productQty = parseInt(searchParams.get('qtd') || '1');

    if (productId && productPrice > 0) {
      setCartItems([{
        product_id: productId,
        name: decodeURIComponent(productName || ''),
        unit_price: productPrice,
        quantity: productQty,
        image_url: productImage ? decodeURIComponent(productImage) : null,
      }]);
    } else {
      // Load from localStorage cart
      const savedCart = localStorage.getItem('aug_cart');
      if (savedCart) {
        try {
          const items = JSON.parse(savedCart);
          setCartItems(items.map(item => ({
            product_id: item.id,
            name: item.name,
            unit_price: item.sale_price || item.regular_price || 0,
            quantity: item.quantity,
            image_url: item.images?.[0] || null,
          })).filter(i => i.unit_price > 0));
        } catch (e) { console.error(e); }
      }
    }
    setLoading(false);
    // GA4: begin_checkout (dispara quando os itens já foram carregados)
  }, [slug, searchParams]);

  // GA4 begin_checkout — dispara quando cartItems estiver pronto
  useEffect(() => {
    if (cartItems.length > 0) {
      const subtotal = cartItems.reduce((sum, i) => sum + i.unit_price * i.quantity, 0);
      trackBeginCheckout({ items: cartItems, total: subtotal, partner: slug });
    }
  }, [cartItems, slug]);

  const formatCep = (v) => {
    const digits = v.replace(/\D/g, '').slice(0, 8);
    return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  };

  const formatPhone = (v) => {
    const digits = v.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const calculateShipping = (cepValue) => {
    const state = getStateFromCep(cepValue);
    if (!state) { setShippingCost(0); setShippingLabel(''); return; }
    const zoneNum = FREIGHT_ZONES[state] || 2;
    const zone = FREIGHT_TABLE[zoneNum];
    setShippingCost(zone.minPrice);
    setShippingLabel(zone.label);
  };

  const handleCepChange = (v) => {
    const formatted = formatCep(v);
    setCep(formatted);
    if (formatted.replace(/\D/g, '').length === 8) calculateShipping(formatted);
    else { setShippingCost(0); setShippingLabel(''); }
  };

  const updateQty = (idx, delta) => {
    setCartItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const newQty = Math.max(1, item.quantity + delta);
      return { ...item, quantity: newQty };
    }));
  };

  const removeItem = (idx) => {
    setCartItems(prev => prev.filter((_, i) => i !== idx));
  };

  const subtotal = cartItems.reduce((sum, i) => sum + i.unit_price * i.quantity, 0);
  const total = subtotal; // Frete não é cobrado no pagamento — combinado via WhatsApp

  const handleSubmit = async () => {
    setError('');
    if (!buyerName.trim()) { setError('Preencha seu nome completo'); return; }
    if (!buyerEmail.trim() || !buyerEmail.includes('@')) { setError('Preencha um email válido'); return; }
    if (cartItems.length === 0) { setError('Carrinho vazio'); return; }

    setSubmitting(true);
    try {
      const phone = buyerPhone.replace(/\D/g, '');
      const res = await fetch(`${API_URL}/api/payments/create-preference`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cartItems,
          partner_slug: slug,
          buyer_name: buyerName.trim(),
          buyer_email: buyerEmail.trim(),
          buyer_phone: phone || null,
          shipping_cost: 0,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Erro ao processar pagamento');
      }

      const data = await res.json();
      // Redirect to Mercado Pago checkout
      const checkoutUrl = data.init_point || data.sandbox_init_point;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        throw new Error('URL de pagamento não recebida');
      }
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#162289]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="checkout-page">
      {/* Header */}
      <div className="bg-[#0a1555] py-3 px-6">
        <div className="max-w-[1000px] mx-auto flex items-center justify-between">
          <Link to={`/parceiros/${slug}`} className="flex items-center gap-3 group" data-testid="back-to-store">
            <ArrowLeft className="w-5 h-5 text-white/60 group-hover:text-white" />
            {partner?.logo_url && (
              <img src={partner.logo_url} alt={partner?.name} className="h-9 w-auto object-contain bg-white rounded-lg p-0.5" />
            )}
            <span className="font-semibold text-white text-sm">{partner?.name || 'Voltar'}</span>
          </Link>
          <div className="flex items-center gap-2 text-white/60 text-sm">
            <Lock className="w-4 h-4" />
            <span>Checkout Seguro</span>
          </div>
        </div>
      </div>

      <div className="max-w-[1000px] mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-3" data-testid="checkout-title">
          <ShoppingCart className="w-6 h-6 text-[#162289]" />
          Finalizar Compra
        </h1>

        {cartItems.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl">
            <ShoppingCart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-600 mb-2">Carrinho vazio</h2>
            <Link to={`/parceiros/${slug}`} className="text-[#162289] hover:underline font-medium" data-testid="continue-shopping">
              Voltar para a loja
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left - Form & Items */}
            <div className="lg:col-span-2 space-y-6">
              {/* Cart Items */}
              <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="cart-items-section">
                <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-[#162289]" />
                  Itens do pedido ({cartItems.length})
                </h2>
                <div className="divide-y divide-gray-100">
                  {cartItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4 py-4" data-testid={`cart-item-${idx}`}>
                      <div className="w-16 h-16 bg-gray-100 rounded-xl flex-shrink-0 overflow-hidden flex items-center justify-center">
                        {item.image_url ? (
                          <img src={item.image_url} alt={item.name} className="w-full h-full object-contain p-1" />
                        ) : (
                          <ShoppingCart className="w-6 h-6 text-gray-300" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 truncate">{item.name}</h3>
                        <p className="text-sm text-[#162289] font-bold mt-1">R$ {item.unit_price.toFixed(2)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => updateQty(idx, -1)} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50" data-testid={`qty-minus-${idx}`}>
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-medium text-sm" data-testid={`qty-value-${idx}`}>{item.quantity}</span>
                        <button onClick={() => updateQty(idx, 1)} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50" data-testid={`qty-plus-${idx}`}>
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-sm font-bold text-gray-900 w-24 text-right">R$ {(item.unit_price * item.quantity).toFixed(2)}</p>
                      <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600 transition-colors" data-testid={`remove-item-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Buyer Info */}
              <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="buyer-info-section">
                <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-[#162289]" />
                  Dados do comprador
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome completo *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text" value={buyerName} onChange={e => setBuyerName(e.target.value)}
                        placeholder="Seu nome completo"
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#162289]/30 focus:border-[#162289]"
                        data-testid="buyer-name-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="email" value={buyerEmail} onChange={e => setBuyerEmail(e.target.value)}
                        placeholder="seu@email.com"
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#162289]/30 focus:border-[#162289]"
                        data-testid="buyer-email-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="tel" value={buyerPhone} onChange={e => setBuyerPhone(formatPhone(e.target.value))}
                        placeholder="(63) 99999-9999"
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#162289]/30 focus:border-[#162289]"
                        data-testid="buyer-phone-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Shipping */}
              <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="shipping-section">
                <h2 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                  <Truck className="w-5 h-5 text-[#162289]" />
                  Estimativa de Frete
                </h2>
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <p className="text-amber-800 text-xs font-medium">⚠️ O frete <strong>não é cobrado</strong> no pagamento. O valor abaixo é apenas uma estimativa e pode não atender sua região. Após a compra, entre em contato via WhatsApp para combinar o envio e o valor real do frete.</p>
                </div>
                <div className="flex gap-3">
                  <input
                    type="text" value={cep} onChange={e => handleCepChange(e.target.value)}
                    placeholder="CEP: 00000-000"
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#162289]/30 focus:border-[#162289]"
                    maxLength={9}
                    data-testid="checkout-cep-input"
                  />
                </div>
                {shippingLabel && (
                  <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl p-3" data-testid="shipping-result">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-blue-800 font-semibold text-xs">📦 Correios PAC — {shippingLabel}</span>
                    </div>
                    <p className="text-blue-700 text-xs">A partir de <strong>R$ {shippingCost},00</strong> conforme peso do produto.</p>
                    <p className="text-blue-600 text-xs mt-1">⚠️ Valor aproximado. O frete <strong>não é cobrado</strong> aqui — finalizamos pelo WhatsApp com valor exato e prazo confirmado.</p>
                    <a href={`https://wa.me/556333224310?text=${encodeURIComponent('🟢 Olá! Vim do site, tenho interesse em comprar peças. Gostaria de confirmar o frete para o meu CEP.')}`}
                      target="_blank" rel="noopener noreferrer"
                      className="mt-2 flex items-center gap-1 text-xs text-green-700 font-semibold hover:text-green-900 transition-colors">
                      <MessageCircle className="w-3 h-3" /> Falar com atendente no WhatsApp
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Right - Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-6" data-testid="order-summary">
                <h2 className="font-semibold text-gray-900 mb-4">Resumo do pedido</h2>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal ({cartItems.reduce((s, i) => s + i.quantity, 0)} itens)</span>
                    <span>R$ {subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Frete</span>
                    <span className="text-amber-600 font-medium text-xs">A combinar via WhatsApp</span>
                  </div>
                  <div className="border-t border-gray-100 pt-3 flex justify-between font-bold text-lg text-gray-900">
                    <span>Total</span>
                    <span data-testid="order-total">R$ {total.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    até 10x de R$ {(total / 10).toFixed(2)} no cartão
                  </p>
                </div>

                {/* Payment Methods */}
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Formas de pagamento</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 text-xs bg-gray-50 text-gray-600 px-2.5 py-1.5 rounded-lg">
                      <CreditCard className="w-3.5 h-3.5" /> Cartão até 10x
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs bg-gray-50 text-gray-600 px-2.5 py-1.5 rounded-lg">
                      <QrCode className="w-3.5 h-3.5" /> PIX
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs bg-gray-50 text-gray-600 px-2.5 py-1.5 rounded-lg">
                      <FileText className="w-3.5 h-3.5" /> Boleto
                    </span>
                  </div>
                </div>

                {error && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-3 text-red-600 text-sm" data-testid="checkout-error">
                    {error}
                  </div>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={submitting || cartItems.length === 0}
                  className="w-full mt-5 py-4 bg-[#009ee3] hover:bg-[#008bcf] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-bold text-base transition-colors flex items-center justify-center gap-2"
                  data-testid="pay-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4" />
                      Pagar com Mercado Pago
                    </>
                  )}
                </button>

                <div className="mt-4 flex items-center justify-center gap-2 text-gray-400 text-xs">
                  <Shield className="w-4 h-4" />
                  <span>Pagamento seguro via Mercado Pago</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutPage;
